import axios from 'axios';

import getRunnerApiUrl from '@/utils/getRunnerApiUrl';
import {
	SET_OPPORTUNITIES_SETTINGS,
	SET_OPPORTUNITY_STATUS_LABEL,
	SET_ALL_OPPORTUNITY_STATUS_LABELS,
	SET_OPPORTUNITIES_LOADING,
	SET_OPPORTUNITIES_ERROR,
} from './types';
import type {
	OpportunitiesSettings,
	OpportunityStatusLabelMap,
} from '@/types/models/opportunities';
import type { RootState } from '@/reducers';

const runnerApiUrl = getRunnerApiUrl(window.location.href);

// Single-flight guard: ensures that if multiple components trigger the fetch at
// the same time, only one network request is made and all callers await the
// same Promise. This avoids redundant requests and race conditions.
let inFlightOpportunitiesSettingsRequest: Promise<OpportunitiesSettings | null> | null =
	null;

// Fetch opportunities settings with single-flight deduplication. If a request
// is already in-flight, reuse it. Once the request settles (success or error),
// clear the guard so future calls can refetch when needed.
export const getOpportunitiesSettings = async () => {
	if (inFlightOpportunitiesSettingsRequest) {
		return inFlightOpportunitiesSettingsRequest;
	}

	inFlightOpportunitiesSettingsRequest = (async () => {
		try {
			const response = await axios.get(
				`${runnerApiUrl}/opportunities/settings`
			);

			if (response.status !== 200) {
				throw new Error('Failed to get opportunities settings');
			}

			return response.data as OpportunitiesSettings;
		} catch (error) {
			console.error('Error getting opportunities settings:', error);
			return null;
		} finally {
			inFlightOpportunitiesSettingsRequest = null;
		}
	})();

	return inFlightOpportunitiesSettingsRequest;
};

export const setAllOpportunityStatusLabels = (
	labels: OpportunityStatusLabelMap
) => ({
	type: SET_ALL_OPPORTUNITY_STATUS_LABELS,
	payload: labels,
});

export const setOpportunityStatusLabel = (status: string, label: string) => ({
	type: SET_OPPORTUNITY_STATUS_LABEL,
	payload: { status, label },
});

export const setOpportunitiesLoading = (loading: boolean) => ({
	type: SET_OPPORTUNITIES_LOADING,
	payload: loading,
});

export const setOpportunitiesError = (message: string | null) => ({
	type: SET_OPPORTUNITIES_ERROR,
	payload: message,
});

export const setOpportunitiesSettings = (settings: OpportunitiesSettings) => ({
	type: SET_OPPORTUNITIES_SETTINGS,
	payload: settings,
});

export const fetchOpportunitiesSettings =
	() =>
	async (dispatch: (action: unknown) => void, getState: () => RootState) => {
		// Avoid redundant work: if we're already loading, or we already have
		// settings in the store, bail out. This prevents multiple components
		// from triggering duplicate fetches on mount.
		const state = getState();
		const { loading, settings } = state.opportunities || {};
		if (loading || settings) return;

		try {
			dispatch(setOpportunitiesLoading(true));
			const data = await getOpportunitiesSettings();
			if (!data) {
				dispatch(
					setOpportunitiesError(
						'Failed to get opportunities settings'
					)
				);
				return;
			}
			dispatch(setOpportunitiesSettings(data as OpportunitiesSettings));
		} catch (error) {
			const message =
				(error instanceof Error && error.message) ||
				'Failed to get opportunities settings';
			dispatch(setOpportunitiesError(message));
		} finally {
			dispatch(setOpportunitiesLoading(false));
		}
	};

export const persistOpportunityStatusLabel =
	({ status, label }: { status: string; label: string }) =>
	async (dispatch: (action: unknown) => void) => {
		try {
			await axios.patch(
				`${runnerApiUrl}/opportunities/settings/status-labels`,
				{ status, label }
			);
		} catch (error) {
			const message =
				(error instanceof Error && error.message) ||
				'Failed to save status label';
			dispatch(setOpportunitiesError(message));
		}
	};
